/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * CalendarScreen — premium unified calendar for the services-app Management mode.
 * Feature parity with the admin /calendar/main page: three data modes
 * (My own / Shared / Busy), Month / Week / Agenda views, a premium toolbar
 * (prev / today / next + jump label), event create/edit with a conflict guard,
 * a "Foglalt vagyok" quick mark-busy shortcut, restaurant-scoped expert
 * selection with avatars and read-only domain feed layers.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView } from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import ManagementScreen from '../components/management/ManagementScreen';
import { GradientHero, EmptyState, InlineError, SectionTitle, Fab } from '../components/management/ManagementUI';
import Icon from '../components/management/SafeIcon';
import Avatar from '../components/Avatar';
import { useManagementTheme } from '../helpers/managementTheme';
import { PillTabs } from '../components/premium';
import EventEditModal from '../components/EventEditModal';
import ConfirmationModal from '../components/ConfirmationModal';
import MonthlyCalendarView from './MonthlyCalendarView';
import calendarApi, { combineDateTime, getCurrentUserId } from '../helpers/calendarApi';
import { syncAlarmReminders, cancelEventReminders } from '../helpers/calendarReminders';

const FEED_SOURCES = ['tasks', 'projects', 'bookings', 'service', 'hr', 'complaints', 'mail'];
const VIEW_OPTIONS = ['month', 'week', 'agenda'];

const pad = (n) => String(n).padStart(2, '0');
const dateKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const monthRange = (date) => ({
    start: new Date(date.getFullYear(), date.getMonth(), 1).toISOString(),
    end: new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59).toISOString()
});

const sameDay = (a, b) => a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();

const startOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = day === 0 ? 6 : day - 1; // Monday-first

    d.setDate(d.getDate() - diff);
    d.setHours(0, 0, 0, 0);

    return d;
};

const splitIso = (iso) => {
    const d = new Date(iso);

    return {
        date: dateKey(d),
        time: `${pad(d.getHours())}:${pad(d.getMinutes())}`
    };
};

export default function CalendarScreen() {
    const { t, i18n } = useTranslation();
    const m = useManagementTheme();
    const navigation = useNavigation();
    const route = useRoute();
    // Optional deep-link params (e.g. from a to-do): jump to `focusDate` and open the `focusEventId` event once.
    const focusEventId = route.params?.focusEventId ? String(route.params.focusEventId) : '';
    const focusDate = route.params?.focusDate ? String(route.params.focusDate) : '';
    const focusHandled = useRef('');

    const [viewMode, setViewMode] = useState('my');
    const [calendarView, setCalendarView] = useState('month');
    const [experts, setExperts] = useState([]);
    const [selectedExpertIds, setSelectedExpertIds] = useState([]);
    const [layers, setLayers] = useState([]);
    const [showLayers, setShowLayers] = useState(false);
    const [events, setEvents] = useState([]);
    const [connection, setConnection] = useState({ google: false, apple: false });
    const [currentUserId, setCurrentUserId] = useState(null);

    const [selectedDate, setSelectedDate] = useState(new Date());
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState('');
    const [toast, setToast] = useState('');

    const [modalVisible, setModalVisible] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [modalInitial, setModalInitial] = useState(null);
    const [busyMode, setBusyMode] = useState(false);
    const [saving, setSaving] = useState(false);

    const [conflictVisible, setConflictVisible] = useState(false);
    const [pendingSave, setPendingSave] = useState(null);

    const range = useMemo(() => monthRange(selectedDate), [selectedDate]);

    useEffect(() => {
        const d = focusDate ? new Date(focusDate) : null;

        if (d && !Number.isNaN(d.getTime())) {
            setViewMode('my');
            setSelectedDate(d);
        }
    }, [focusDate, focusEventId]);

    useEffect(() => {
        (async () => {
            setCurrentUserId(await getCurrentUserId());
            const status = await calendarApi.fetchConnectionStatus().catch(() => null);

            if (status) {
                setConnection({
                    google: Boolean(status.google?.connected),
                    apple: Boolean(status.apple?.connected)
                });
            }
            const list = await calendarApi.fetchExperts().catch(() => []);

            setExperts(list);
        })();
    }, []);

    /**
     * Fire-and-forget reconciliation of the phone's LOCAL alarm reminders from
     * a dedicated 14-day look-ahead window (independent of the visible month —
     * matching the widget summary's horizon), so browsing a past month never
     * prunes alarms belonging to upcoming events. fetchMyEvents returns each
     * event's full `notifications` block (reminders included), which is exactly
     * what the engine consumes; quiet 'notification'-style rows stay the
     * backend cron's job and are never scheduled locally.
     */
    const syncReminderSchedule = useCallback(() => {
        const start = new Date();
        const end = new Date(start.getTime() + 14 * 24 * 60 * 60 * 1000);

        calendarApi.fetchMyEvents({ start: start.toISOString(), end: end.toISOString() })
            .then((list) => syncAlarmReminders(Array.isArray(list) ? list : []))
            .catch(() => { /* Best-effort — the next load/save retries. */ });
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            let primary = [];

            if (viewMode === 'my') {
                primary = await calendarApi.fetchMyEvents(range);
            } else if (viewMode === 'shared') {
                const payloads = await calendarApi.fetchShared({
                    expertIds: selectedExpertIds,
                    start: range.start,
                    end: range.end
                });

                payloads.forEach((p) => {
                    (p.events || []).forEach((e) => primary.push({ ...e, _expertColor: p.expert?.color }));
                    (p.availability?.unavailability || []).forEach((r, i) => {
                        if (r.start && r.end) {
                            primary.push({
                                _id: `unavail-${p.expert?._id}-${i}`,
                                title: r.reason || t('calendar_unavailable'),
                                start: r.start,
                                end: r.end,
                                _readOnly: true,
                                _blocked: true
                            });
                        }
                    });
                });
            } else if (viewMode === 'busy') {
                const availability = await calendarApi.fetchAvailability().catch(() => null);

                (availability?.unavailability || []).forEach((r, i) => {
                    if (r.start && r.end) {
                        primary.push({
                            _id: `my-unavail-${i}`,
                            title: r.reason || t('calendar_unavailable'),
                            start: r.start,
                            end: r.end,
                            _readOnly: true,
                            _blocked: true
                        });
                    }
                });
            }

            if (layers.length > 0) {
                const feed = await calendarApi.fetchFeed({
                    start: range.start,
                    end: range.end,
                    sources: layers
                });

                feed.forEach((f) => primary.push({
                    ...f,
                    _readOnly: true,
                    _layer: f.source
                }));
            }

            setEvents(primary);
            // Fire-and-forget: reconcile the locally scheduled alarm reminders
            // on every load/refresh (save/delete land here too via load()).
            syncReminderSchedule();
        } catch (err) {
            setError(err?.message || t('calendar_load_failed'));
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [viewMode, selectedExpertIds, range, layers, t, syncReminderSchedule]);

    useEffect(() => {
        load();
    }, [load]);

    const bookings = useMemo(() => events
        .filter((e) => e.start)
        .map((e) => ({
            ...e,
            dateFrom: e.start,
            status: e._blocked ? 'rejected' : (e._readOnly ? 'pending' : 'accepted')
        })), [events]);

    const dayEvents = useMemo(() => events
        .filter((e) => e.start && sameDay(new Date(e.start), selectedDate))
        .sort((a, b) => new Date(a.start) - new Date(b.start)), [events, selectedDate]);

    const agendaGroups = useMemo(() => {
        const sorted = events
            .filter((e) => e.start)
            .sort((a, b) => new Date(a.start) - new Date(b.start));
        const groups = [];
        const byKey = {};

        sorted.forEach((e) => {
            const key = dateKey(new Date(e.start));

            if (!byKey[key]) {
                byKey[key] = { key, date: new Date(e.start), items: [] };
                groups.push(byKey[key]);
            }
            byKey[key].items.push(e);
        });

        return groups;
    }, [events]);

    const weekDays = useMemo(() => {
        const start = startOfWeek(selectedDate);

        return Array.from({ length: 7 }, (_, i) => {
            const d = new Date(start);

            d.setDate(start.getDate() + i);

            return {
                date: d,
                count: events.filter((e) => e.start && sameDay(new Date(e.start), d)).length
            };
        });
    }, [selectedDate, events]);

    const toggleExpert = (id) => {
        setSelectedExpertIds((prev) => (prev.includes(id)
            ? prev.filter((x) => x !== id)
            : [...prev, id]));
    };

    const toggleLayer = (src) => {
        setLayers((prev) => (prev.includes(src) ? prev.filter((s) => s !== src) : [...prev, src]));
    };

    const shiftPeriod = (dir) => {
        setSelectedDate((prev) => {
            const d = new Date(prev);

            if (calendarView === 'week') {
                d.setDate(d.getDate() + dir * 7);
            } else if (calendarView === 'agenda') {
                d.setMonth(d.getMonth() + dir);
            } else {
                d.setMonth(d.getMonth() + dir);
            }

            return d;
        });
    };

    const periodLabel = useMemo(() => {
        const locale = i18n.language === 'hu' ? 'hu-HU' : i18n.language;

        if (calendarView === 'week') {
            const start = startOfWeek(selectedDate);
            const end = new Date(start);

            end.setDate(start.getDate() + 6);

            return `${start.toLocaleDateString(locale, { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString(locale, { month: 'short', day: 'numeric' })}`;
        }

        return selectedDate.toLocaleDateString(locale, { year: 'numeric', month: 'long' });
    }, [selectedDate, calendarView, i18n.language]);

    const openCreate = () => {
        const dateStr = dateKey(selectedDate);

        setBusyMode(false);
        setModalMode('create');
        setModalInitial({
            startDate: dateStr,
            startTime: '09:00',
            endDate: dateStr,
            endTime: '10:00',
            reminderMinutes: 30,
            notificationsEnabled: true,
            notifyByEmail: true,
            notifyBySms: false,
            notifyExpert: true,
            expertId: viewMode === 'shared' ? (selectedExpertIds[0] || '') : ''
        });
        setModalVisible(true);
    };

    const openMarkBusy = () => {
        const dateStr = dateKey(selectedDate);

        setBusyMode(true);
        setModalMode('create');
        setModalInitial({
            startDate: dateStr,
            startTime: '09:00',
            endDate: dateStr,
            endTime: '10:00'
        });
        setModalVisible(true);
    };

    useEffect(() => {
        if (!focusEventId || loading || focusHandled.current === focusEventId) {
            return;
        }

        const hit = events.find((e) => String(e._id) === focusEventId);

        if (hit) {
            focusHandled.current = focusEventId;
            openEdit(hit);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [events, loading, focusEventId]);

    const openEdit = (event) => {
        if (event._layer === 'mail' && event.meta?.messageId) {
            navigation.navigate('MailThreadScreen', {
                messageId: event.meta.messageId,
                threadKey: event.meta.threadKey,
                subject: event.meta.subject
            });

            return;
        }

        if (event._readOnly) {
            return;
        }
        const s = splitIso(event.start);
        const e = splitIso(event.end || event.start);

        setBusyMode(false);
        setModalMode('edit');
        setModalInitial({
            id: event._id,
            title: event.title,
            description: event.description,
            startDate: s.date,
            startTime: s.time,
            endDate: e.date,
            endTime: e.time,
            expertId: event.expert?._id || '',
            clientName: event.client || '',
            clientPhone: event.clientPhone || '',
            clientEmail: event.clientEmail || '',
            reminderMinutes: event.notifications?.reminderTime ?? 30,
            reminders: Array.isArray(event.notifications?.reminders) ? event.notifications.reminders : undefined,
            notificationsEnabled: event.notifications?.enabled !== false,
            notifyByEmail: event.notifications?.channels?.email !== false,
            notifyBySms: Boolean(event.notifications?.channels?.sms),
            notifyExpert: event.notifications?.notifyExpert !== false,
            syncWithGoogle: Boolean(event.syncedWithGoogle),
            syncWithApple: Boolean(event.syncedWithApple),
            meetingUrl: event.meetingUrl || '',
            meetingProvider: event.meetingProvider || ''
        });
        setModalVisible(true);
    };

    const buildPayload = (values, confirmConflict = false) => {
        const isShared = viewMode === 'shared';
        const payload = {
            title: values.title,
            description: values.description,
            start: combineDateTime(values.startDate, values.startTime),
            end: combineDateTime(values.endDate, values.endTime),
            type: isShared ? 'expert' : 'personal',
            notifications: {
                enabled: values.notificationsEnabled,
                reminderTime: Number(values.reminderMinutes) || 30,
                // Multi-reminder rows (additive — dropped from the JSON when the
                // modal did not emit any, so legacy payload shapes are untouched).
                reminders: Array.isArray(values.reminders) && values.reminders.length ? values.reminders : undefined,
                notifyExpert: isShared ? values.notifyExpert : undefined,
                channels: {
                    email: values.notifyByEmail,
                    sms: Boolean(values.notifyBySms),
                    push: true
                }
            },
            syncedWithGoogle: values.syncWithGoogle,
            syncedWithApple: values.syncWithApple,
            // First-class online meeting link ('' clears it on edit); the backend
            // re-detects the provider from the URL when it is left empty.
            meetingUrl: (values.meetingUrl || '').trim(),
            meetingProvider: values.meetingProvider || ''
        };

        if (isShared && values.expertId) {
            payload.expert = { _id: values.expertId };
            payload.client = values.clientName;
            payload.clientEmail = values.clientEmail;
            payload.clientPhone = values.clientPhone;
            if (confirmConflict) {
                payload.confirmConflict = true;
            }
        } else {
            payload.owner = currentUserId;
        }

        return payload;
    };

    const persist = async (values, confirmConflict = false) => {
        setSaving(true);
        try {
            const payload = buildPayload(values, confirmConflict);

            if (modalMode === 'edit' && modalInitial?.id) {
                await calendarApi.updateEvent(modalInitial.id, payload);
            } else {
                await calendarApi.createEvent(payload);
            }
            setModalVisible(false);
            setPendingSave(null);
            await load();
        } catch (err) {
            setError(err?.message || t('calendar_save_failed'));
        } finally {
            setSaving(false);
        }
    };

    const handleSave = async (values) => {
        if (busyMode) {
            setSaving(true);
            try {
                await calendarApi.markMyselfBusy({
                    start: combineDateTime(values.startDate, values.startTime),
                    end: combineDateTime(values.endDate, values.endTime),
                    reason: values.title
                });
                setModalVisible(false);
                setToast(t('calendar_marked_busy'));
                globalThis.setTimeout(() => setToast(''), 2500);
                if (viewMode === 'busy') {
                    await load();
                }
            } catch (err) {
                setError(err?.message || t('calendar_save_failed'));
            } finally {
                setSaving(false);
            }

            return;
        }

        if (!values.title || !values.startDate || !values.endDate) {
            setError(t('calendar_missing_fields'));

            return;
        }
        const isShared = viewMode === 'shared';

        if (isShared && values.expertId) {
            const conflict = await calendarApi.checkConflict({
                expertId: values.expertId,
                start: combineDateTime(values.startDate, values.startTime),
                end: combineDateTime(values.endDate, values.endTime),
                excludeEventId: modalMode === 'edit' ? modalInitial?.id : undefined
            }).catch(() => null);

            if (conflict?.hasConflict) {
                setPendingSave(values);
                setConflictVisible(true);

                return;
            }
        }
        await persist(values, false);
    };

    const handleDelete = async () => {
        if (!modalInitial?.id) {
            return;
        }
        setSaving(true);
        try {
            await calendarApi.deleteEvent(modalInitial.id);
            // Retire this event's local alarm reminders immediately (the load()
            // below re-syncs the rest of the schedule).
            cancelEventReminders(String(modalInitial.id)).catch(() => {});
            setModalVisible(false);
            await load();
        } catch (err) {
            setError(err?.message || t('calendar_save_failed'));
        } finally {
            setSaving(false);
        }
    };

    const modeOptions = [
        { value: 'my', label: t('calendar_mode_my'), icon: 'account' },
        { value: 'shared', label: t('calendar_mode_shared'), icon: 'account-group' },
        { value: 'busy', label: t('calendar_mode_busy'), icon: 'cancel' }
    ];

    /**
     * Compact lead-time label for the event-card reminder line ("15 p",
     * "1 ó", "1 nap", "kezdéskor").
     * @param {number} minutesBefore - Lead time in minutes before start.
     * @returns {string}
     */
    const formatLeadShort = (minutesBefore) => {
        const mins = Math.max(0, Math.floor(Number(minutesBefore)) || 0);

        if (!mins) {
            return t('calendar_reminder_at_start_short', { defaultValue: 'kezdéskor' });
        }
        if (mins % 1440 === 0) {
            return t('calendar_reminder_days_short', { defaultValue: '{{count}} nap', count: mins / 1440 });
        }
        if (mins % 60 === 0) {
            return t('calendar_reminder_hours_short', { defaultValue: '{{count}} ó', count: mins / 60 });
        }

        return t('calendar_reminder_minutes_short', { defaultValue: '{{count}} p', count: mins });
    };

    // Premium calendar event card — a time column, a colour-coded accent bar (busy = danger,
    // read-only feed = warning, own event = brand orange) and the title/meta. Reads far more
    // like a native calendar than a generic list row.
    const renderEventRow = (event) => {
        const accent = event._blocked ? m.colors.danger : (event._readOnly ? m.colors.warning : m.colors.primary);
        const start = splitIso(event.start).time;
        const end = event.end ? splitIso(event.end).time : '';
        const metaParts = [event.client, event._blocked ? t('calendar_legend_busy') : (event._layer ? t(`calendar_layer_${event._layer}`) : '')].filter(Boolean);
        // Bell + lead-time line: the event's reminder rows (multi-reminder array,
        // else the legacy single reminderTime), shown on own editable events with
        // notifications enabled.
        const eventReminders = !event._readOnly && event.notifications && event.notifications.enabled !== false
            ? (Array.isArray(event.notifications.reminders) && event.notifications.reminders.length
                ? event.notifications.reminders
                : (event.notifications.reminderTime != null
                    ? [{ minutesBefore: event.notifications.reminderTime, style: 'notification' }]
                    : []))
            : [];
        const reminderLabel = eventReminders.map((r) => formatLeadShort(r?.minutesBefore)).join(' · ');
        const hasAlarmReminder = eventReminders.some((r) => r?.style === 'alarm');

        return (
            <TouchableOpacity
                key={String(event._id)}
                activeOpacity={event._readOnly && event._layer !== 'mail' ? 1 : 0.7}
                onPress={() => openEdit(event)}
                style={[styles.eventCard, { backgroundColor: m.colors.surface, borderColor: m.colors.border }, m.shadow(1)]}
            >
                <View style={[styles.eventAccent, { backgroundColor: accent }]} />
                <View style={styles.eventTimeCol}>
                    <Text style={[styles.eventTimeStart, { color: m.colors.heading }]}>{start}</Text>
                    {!!end && <Text style={[styles.eventTimeEnd, { color: m.colors.subText }]}>{end}</Text>}
                </View>
                <View style={styles.eventBody}>
                    <Text style={[styles.eventTitle, { color: m.colors.heading }]} numberOfLines={1}>
                        {event.title || t('calendar_create_event')}
                    </Text>
                    {metaParts.length > 0 && (
                        <Text style={[styles.eventMeta, { color: m.colors.subText }]} numberOfLines={1}>
                            {metaParts.join(' · ')}
                        </Text>
                    )}
                    {!!reminderLabel && (
                        <View style={styles.eventReminderLine}>
                            <Icon name={hasAlarmReminder ? 'alarm' : 'bell-outline'} size={12} color={m.colors.subText} />
                            <Text style={[styles.eventReminderText, { color: m.colors.subText }]} numberOfLines={1}>
                                {reminderLabel}
                            </Text>
                        </View>
                    )}
                </View>
                {event._blocked ? (
                    <Icon name="lock-outline" size={16} color={m.colors.danger} />
                ) : (event._readOnly ? (
                    <Icon name="layers-outline" size={16} color={m.colors.warning} />
                ) : (
                    <Icon name="chevron-right" size={20} color={m.colors.subText} />
                ))}
            </TouchableOpacity>
        );
    };

    return (
        <ManagementScreen safeTop
            refreshing={refreshing}
            onRefresh={() => {
                setRefreshing(true);
                load();
            }}
            floating={<Fab icon="plus" label={t('calendar_create_event')} onPress={openCreate} />}
        >
            <GradientHero
                eyebrow={t('calendar_eyebrow')}
                title={t('calendar_title')}
                icon="plus"
                onIconPress={openCreate}
                secondaryIcon="cog-outline"
                onSecondaryPress={() => navigation.navigate('CalendarSettingsScreen')}
            />

            {/* Mode filter — My own / Shared / Busy */}
            <View style={styles.modeTabs}>
                <PillTabs
                    variant="solid"
                    options={modeOptions.map((opt) => ({ value: opt.value, label: opt.label, icon: opt.icon }))}
                    value={viewMode}
                    onChange={setViewMode}
                />
            </View>

            {/* Premium toolbar — navigation + period + view switch */}
            <View style={styles.toolbar}>
                <View style={styles.navGroup}>
                    <TouchableOpacity style={[styles.navBtn, { borderColor: m.colors.border }]} onPress={() => shiftPeriod(-1)}>
                        <Icon name="chevron-left" size={20} color={m.colors.text} />
                    </TouchableOpacity>
                    <TouchableOpacity
                        style={[styles.todayBtn, { borderColor: m.colors.border }]}
                        onPress={() => setSelectedDate(new Date())}
                    >
                        <Text style={[styles.todayText, { color: m.colors.text }]}>{t('calendar_today')}</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={[styles.navBtn, { borderColor: m.colors.border }]} onPress={() => shiftPeriod(1)}>
                        <Icon name="chevron-right" size={20} color={m.colors.text} />
                    </TouchableOpacity>
                </View>
                <Text style={[styles.periodLabel, { color: m.colors.heading }]} numberOfLines={1}>{periodLabel}</Text>
            </View>

            <View style={styles.viewSwitch}>
                <PillTabs
                    options={VIEW_OPTIONS.map((v) => ({ value: v, label: t(`calendar_view_${v}`) }))}
                    value={calendarView}
                    onChange={setCalendarView}
                />
            </View>

            {/* Quick "I'm busy" shortcut (hidden in shared mode) */}
            {viewMode !== 'shared' && (
                <TouchableOpacity
                    style={[styles.busyBtn, { borderColor: m.colors.danger }]}
                    onPress={openMarkBusy}
                >
                    <Icon name="account-clock" size={16} color={m.colors.danger} />
                    <Text style={{ color: m.colors.danger, fontWeight: '700' }}>{t('calendar_mark_busy_action')}</Text>
                </TouchableOpacity>
            )}

            {/* Shared-mode expert picker with avatars */}
            {viewMode === 'shared' && (
                <View style={styles.section}>
                    <Text style={[styles.sectionLabel, { color: m.colors.subText }]}>{t('calendar_select_experts')}</Text>
                    {experts.length === 0 ? (
                        <Text style={[styles.legend, { color: m.colors.subText }]}>{t('calendar_no_experts')}</Text>
                    ) : (
                        <View style={styles.chipRow}>
                            {experts.map((e) => {
                                const id = String(e._id);
                                const active = selectedExpertIds.includes(id);
                                const name = e.displayName || `${e.name?.first || ''} ${e.name?.last || ''}`.trim();

                                return (
                                    <TouchableOpacity
                                        key={id}
                                        onPress={() => toggleExpert(id)}
                                        style={[styles.expertChip, {
                                            borderColor: active ? (e.color || m.colors.primary) : m.colors.border,
                                            backgroundColor: active ? `${e.color || m.colors.primary}1f` : 'transparent'
                                        }]}
                                    >
                                        <Avatar user={e} size={22} />
                                        <Text style={{ color: active ? (e.color || m.colors.primary) : m.colors.subText, fontWeight: '600' }}>
                                            {name}
                                        </Text>
                                    </TouchableOpacity>
                                );
                            })}
                        </View>
                    )}
                </View>
            )}

            {/* Domain feed layers (collapsible) */}
            <TouchableOpacity style={styles.layersToggle} onPress={() => setShowLayers((s) => !s)}>
                <Icon name="layers-outline" size={16} color={m.colors.subText} />
                <Text style={[styles.sectionLabel, { color: m.colors.subText, marginBottom: 0 }]}>{t('calendar_layers_label')}</Text>
                <Icon name={showLayers ? 'chevron-up' : 'chevron-down'} size={18} color={m.colors.subText} />
            </TouchableOpacity>
            {showLayers && (
                <View style={[styles.section, { marginTop: 6 }]}>
                    <View style={styles.chipRow}>
                        {FEED_SOURCES.map((src) => {
                            const active = layers.includes(src);

                            return (
                                <TouchableOpacity
                                    key={src}
                                    onPress={() => toggleLayer(src)}
                                    style={[styles.chip, {
                                        borderColor: active ? m.colors.primary : m.colors.border,
                                        backgroundColor: active ? `${m.colors.primary}22` : 'transparent'
                                    }]}
                                >
                                    <Text style={{ color: active ? m.colors.primary : m.colors.subText }}>
                                        {t(`calendar_layer_${src}`)}
                                    </Text>
                                </TouchableOpacity>
                            );
                        })}
                    </View>
                </View>
            )}

            {error ? <InlineError message={error} /> : null}
            {toast ? (
                <View style={[styles.toast, { backgroundColor: `${m.colors.success}22`, borderColor: m.colors.success }]}>
                    <Icon name="check-circle" size={16} color={m.colors.success} />
                    <Text style={{ color: m.colors.success, fontWeight: '700' }}>{toast}</Text>
                </View>
            ) : null}

            {/* MONTH VIEW */}
            {calendarView === 'month' && (
                <MonthlyCalendarView
                    bookings={bookings}
                    selectedDate={selectedDate}
                    onDateSelect={setSelectedDate}
                    onViewChange={() => {}}
                    refreshing={loading}
                    onRefresh={load}
                    showHeader={false}
                />
            )}

            {/* WEEK VIEW */}
            {calendarView === 'week' && (
                <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.weekStrip}>
                    {weekDays.map(({ date, count }) => {
                        const active = sameDay(date, selectedDate);
                        const isToday = sameDay(date, new Date());
                        const locale = i18n.language === 'hu' ? 'hu-HU' : i18n.language;

                        return (
                            <TouchableOpacity
                                key={dateKey(date)}
                                onPress={() => setSelectedDate(new Date(date))}
                                style={[styles.weekDay, {
                                    borderColor: active ? m.colors.primary : m.colors.border,
                                    backgroundColor: active ? m.colors.primary : m.colors.inputBg
                                }]}
                            >
                                <Text style={{ color: active ? '#fff' : m.colors.subText, fontSize: 11, fontWeight: '700' }}>
                                    {date.toLocaleDateString(locale, { weekday: 'short' })}
                                </Text>
                                <Text style={{ color: active ? '#fff' : (isToday ? m.colors.primary : m.colors.heading), fontSize: 18, fontWeight: '800' }}>
                                    {date.getDate()}
                                </Text>
                                {count > 0 && (
                                    <View style={[styles.weekDot, { backgroundColor: active ? '#fff' : m.colors.primary }]} />
                                )}
                            </TouchableOpacity>
                        );
                    })}
                </ScrollView>
            )}

            {/* DAY EVENT LIST (month + week) */}
            {calendarView !== 'agenda' && (
                <View style={styles.section}>
                    <SectionTitle title={selectedDate.toLocaleDateString(i18n.language === 'hu' ? 'hu-HU' : i18n.language, { weekday: 'long', month: 'long', day: 'numeric' })} />
                    {dayEvents.length === 0 ? (
                        <EmptyState icon="calendar-blank" title={t('calendar_no_events')} />
                    ) : (
                        dayEvents.map(renderEventRow)
                    )}
                </View>
            )}

            {/* AGENDA VIEW */}
            {calendarView === 'agenda' && (
                <View style={styles.section}>
                    {agendaGroups.length === 0 ? (
                        <EmptyState icon="calendar-blank" title={t('calendar_no_events')} />
                    ) : (
                        agendaGroups.map((group) => (
                            <View key={group.key} style={{ marginBottom: 8 }}>
                                <SectionTitle title={group.date.toLocaleDateString(i18n.language === 'hu' ? 'hu-HU' : i18n.language, { weekday: 'long', month: 'long', day: 'numeric' })} />
                                {group.items.map(renderEventRow)}
                            </View>
                        ))
                    )}
                </View>
            )}

            <EventEditModal
                visible={modalVisible}
                mode={modalMode}
                busyMode={busyMode}
                initial={modalInitial}
                experts={experts}
                showExpert={viewMode === 'shared' && !busyMode}
                connection={connection}
                saving={saving}
                onClose={() => setModalVisible(false)}
                onSave={handleSave}
                onDelete={handleDelete}
            />

            <ConfirmationModal
                visible={conflictVisible}
                message={t('calendar_conflict_confirm')}
                onCancel={() => {
                    setConflictVisible(false);
                    setPendingSave(null);
                }}
                onConfirm={() => {
                    setConflictVisible(false);
                    if (pendingSave) {
                        persist(pendingSave, true);
                    }
                }}
            />
        </ManagementScreen>
    );
}

const styles = StyleSheet.create({
    modeTabs: {
        marginTop: 14
    },
    segment: {
        flexDirection: 'row',
        borderRadius: 14,
        borderWidth: 1,
        padding: 4,
        marginTop: 14,
        gap: 4
    },
    segmentItem: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 5,
        paddingVertical: 9,
        borderRadius: 10
    },
    segmentLabel: {
        fontSize: 13,
        fontWeight: '700'
    },
    toolbar: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: 14
    },
    navGroup: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6
    },
    navBtn: {
        width: 36,
        height: 36,
        borderRadius: 18,
        borderWidth: 1,
        alignItems: 'center',
        justifyContent: 'center'
    },
    todayBtn: {
        paddingHorizontal: 14,
        height: 36,
        borderRadius: 999,
        borderWidth: 1,
        alignItems: 'center',
        justifyContent: 'center'
    },
    todayText: {
        fontWeight: '700',
        fontSize: 13
    },
    periodLabel: {
        flex: 1,
        textAlign: 'right',
        fontSize: 16,
        fontWeight: '800',
        marginLeft: 10
    },
    viewSwitch: {
        flexDirection: 'row',
        gap: 8,
        marginTop: 12
    },
    viewPill: {
        flex: 1,
        alignItems: 'center',
        paddingVertical: 9,
        borderRadius: 10,
        borderWidth: 1
    },
    busyBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        marginTop: 12,
        paddingVertical: 11,
        borderRadius: 999,
        borderWidth: 1,
        borderStyle: 'dashed'
    },
    section: {
        marginTop: 14
    },
    eventCard: {
        flexDirection: 'row',
        alignItems: 'center',
        borderRadius: 22,
        borderWidth: 1,
        paddingVertical: 10,
        paddingRight: 12,
        marginBottom: 8,
        overflow: 'hidden'
    },
    eventAccent: {
        width: 4,
        alignSelf: 'stretch',
        borderTopLeftRadius: 22,
        borderBottomLeftRadius: 22,
        marginRight: 12
    },
    eventTimeCol: {
        width: 48,
        alignItems: 'flex-start'
    },
    eventTimeStart: {
        fontSize: 14,
        fontWeight: '800'
    },
    eventTimeEnd: {
        fontSize: 11,
        fontWeight: '600',
        marginTop: 1
    },
    eventBody: {
        flex: 1,
        paddingHorizontal: 10
    },
    eventTitle: {
        fontSize: 14.5,
        fontWeight: '700'
    },
    eventMeta: {
        fontSize: 12,
        fontWeight: '600',
        marginTop: 2
    },
    eventReminderLine: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        marginTop: 3
    },
    eventReminderText: {
        fontSize: 11,
        fontWeight: '600'
    },
    sectionLabel: {
        fontSize: 12,
        fontWeight: '700',
        marginBottom: 6,
        textTransform: 'uppercase'
    },
    chipRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8
    },
    chip: {
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 999,
        borderWidth: 1
    },
    expertChip: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 7,
        paddingHorizontal: 8,
        paddingVertical: 5,
        borderRadius: 999,
        borderWidth: 1
    },
    legend: {
        marginTop: 6,
        fontSize: 12
    },
    layersToggle: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        marginTop: 16
    },
    toast: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        marginTop: 12,
        padding: 12,
        borderRadius: 999,
        borderWidth: 1
    },
    weekStrip: {
        gap: 8,
        paddingVertical: 14
    },
    weekDay: {
        width: 52,
        alignItems: 'center',
        paddingVertical: 10,
        borderRadius: 26,
        borderWidth: 1,
        gap: 4
    },
    weekDot: {
        width: 6,
        height: 6,
        borderRadius: 3
    }
});
